import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const CATEGORIES = [
  { value: 'brochure', label: 'Brochure', icon: '📄' },
  { value: 'form', label: 'Form', icon: '📝' },
  { value: 'image', label: 'Image', icon: '🖼️' },
  { value: 'contract', label: 'Contract', icon: '📋' },
  { value: 'other', label: 'Other', icon: '📎' },
];

const FILE_TYPES = {
  'application/pdf': { icon: '📄', label: 'PDF' },
  'image/jpeg': { icon: '🖼️', label: 'JPEG' },
  'image/png': { icon: '🖼️', label: 'PNG' },
  'image/gif': { icon: '🖼️', label: 'GIF' },
  'application/msword': { icon: '📝', label: 'Word' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: '📝', label: 'Word' },
  'application/vnd.ms-excel': { icon: '📊', label: 'Excel' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: '📊', label: 'Excel' },
};

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Upload state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await api.getDocuments(params);
      setDocuments(response.documents || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(files.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
    })));
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    const progress = {};
    
    try {
      for (const fileInfo of uploadFiles) {
        progress[fileInfo.id] = 0;
        setUploadProgress({ ...progress });
        
        await api.uploadDocument({
          file: fileInfo.file,
          category: uploadCategory,
          description: uploadDescription,
          tags: uploadTags.split(',').map(t => t.trim()).filter(Boolean),
        });
        
        progress[fileInfo.id] = 100;
        setUploadProgress({ ...progress });
      }
      
      setUploadModalOpen(false);
      setUploadFiles([]);
      setUploadDescription('');
      setUploadTags('');
      fetchDocuments();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const handleDownload = async (doc) => {
    try {
      const url = await api.getDocumentDownloadUrl(doc.id);
      window.open(url, '_blank');
    } catch (err) {
      setError(err.message || 'Download failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    
    try {
      await api.deleteDocument(doc.id);
      fetchDocuments();
      if (selectedDocument?.id === doc.id) {
        setSelectedDocument(null);
      }
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const getFileIcon = (mimeType) => {
    return FILE_TYPES[mimeType]?.icon || '📎';
  };

  const getCategoryIcon = (category) => {
    return CATEGORIES.find(c => c.value === category)?.icon || '📎';
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Documents</h1>
        <button 
          style={styles.uploadButton}
          onClick={() => setUploadModalOpen(true)}
        >
          📤 Upload
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={styles.categorySelect}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>
              {cat.icon} {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div style={styles.error}>
          {error}
          <button onClick={() => setError(null)} style={styles.errorClose}>✕</button>
        </div>
      )}

      {/* Document Grid */}
      {loading ? (
        <div style={styles.loading}>Loading documents...</div>
      ) : documents.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📁</div>
          <p>No documents yet</p>
          <button onClick={() => setUploadModalOpen(true)} style={styles.emptyButton}>
            Upload your first document
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {documents.map(doc => (
            <div 
              key={doc.id} 
              style={styles.card}
              onClick={() => setSelectedDocument(doc)}
            >
              <div style={styles.cardIcon}>
                {getFileIcon(doc.mime_type)}
              </div>
              <div style={styles.cardContent}>
                <div style={styles.cardTitle} title={doc.original_filename}>
                  {doc.original_filename}
                </div>
                <div style={styles.cardMeta}>
                  <span>{getCategoryIcon(doc.category)} {doc.category}</span>
                  <span>•</span>
                  <span>{formatFileSize(doc.size_bytes)}</span>
                </div>
                <div style={styles.cardDate}>
                  {formatDate(doc.created_at)}
                </div>
                {doc.tags?.length > 0 && (
                  <div style={styles.cardTags}>
                    {doc.tags.slice(0, 3).map((tag, i) => (
                      <span key={i} style={styles.tag}>#{tag}</span>
                    ))}
                    {doc.tags.length > 3 && (
                      <span style={styles.tag}>+{doc.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div style={styles.modalOverlay} onClick={() => !uploading && setUploadModalOpen(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2>Upload Documents</h2>
              <button 
                onClick={() => !uploading && setUploadModalOpen(false)}
                style={styles.modalClose}
                disabled={uploading}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* File Drop Zone */}
              <div style={styles.dropZone}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  style={styles.fileInput}
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" style={styles.dropZoneLabel}>
                  {uploadFiles.length === 0 ? (
                    <>
                      <div style={styles.dropZoneIcon}>📁</div>
                      <p>Click to select files or drag & drop</p>
                      <p style={styles.dropZoneHint}>PDF, images, Word, Excel up to 50MB</p>
                    </>
                  ) : (
                    <div style={styles.fileList}>
                      {uploadFiles.map(file => (
                        <div key={file.id} style={styles.fileItem}>
                          <span>{getFileIcon(file.type)}</span>
                          <span style={styles.fileName}>{file.name}</span>
                          <span style={styles.fileSize}>{formatFileSize(file.size)}</span>
                          {uploading && (
                            <div style={styles.progressBar}>
                              <div 
                                style={{
                                  ...styles.progressFill,
                                  width: `${uploadProgress[file.id] || 0}%`
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </label>
              </div>

              {/* Metadata */}
              {uploadFiles.length > 0 && (
                <div style={styles.metadata}>
                  <div style={styles.formGroup}>
                    <label>Category</label>
                    <select
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      disabled={uploading}
                      style={styles.select}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label>Description (optional)</label>
                    <textarea
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="What's this document about?"
                      disabled={uploading}
                      style={styles.textarea}
                      rows={2}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label>Tags (comma separated)</label>
                    <input
                      type="text"
                      value={uploadTags}
                      onChange={(e) => setUploadTags(e.target.value)}
                      placeholder="e.g. brochure, bmw, 2024"
                      disabled={uploading}
                      style={styles.input}
                    />
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button 
                onClick={() => setUploadModalOpen(false)}
                disabled={uploading}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button 
                onClick={handleUpload}
                disabled={uploadFiles.length === 0 || uploading}
                style={uploading ? styles.uploadingButton : styles.confirmButton}
              >
                {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Detail Modal */}
      {selectedDocument && (
        <div style={styles.modalOverlay} onClick={() => setSelectedDocument(null)}>
          <div style={styles.detailModal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2>Document Details</h2>
              <button onClick={() => setSelectedDocument(null)} style={styles.modalClose}>
                ✕
              </button>
            </div>

            <div style={styles.detailBody}>
              <div style={styles.detailIcon}>
                {getFileIcon(selectedDocument.mime_type)}
              </div>
              
              <div style={styles.detailInfo}>
                <h3 style={styles.detailTitle}>{selectedDocument.original_filename}</h3>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Category:</span>
                  <span>{getCategoryIcon(selectedDocument.category)} {selectedDocument.category}</span>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Size:</span>
                  <span>{formatFileSize(selectedDocument.size_bytes)}</span>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Type:</span>
                  <span>{selectedDocument.mime_type}</span>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Uploaded:</span>
                  <span>{formatDate(selectedDocument.created_at)}</span>
                </div>
                
                {selectedDocument.description && (
                  <div style={styles.detailDescription}>
                    <span style={styles.detailLabel}>Description:</span>
                    <p>{selectedDocument.description}</p>
                  </div>
                )}
                
                {selectedDocument.tags?.length > 0 && (
                  <div style={styles.detailTags}>
                    <span style={styles.detailLabel}>Tags:</span>
                    <div>
                      {selectedDocument.tags.map((tag, i) => (
                        <span key={i} style={styles.detailTag}>#{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button 
                onClick={() => handleDelete(selectedDocument)}
                style={styles.deleteButton}
              >
                🗑️ Delete
              </button>
              <button 
                onClick={() => handleDownload(selectedDocument)}
                style={styles.downloadButton}
              >
                ⬇️ Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#f1f5f9',
    margin: 0,
  },
  uploadButton: {
    padding: '12px 24px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    minWidth: '200px',
    padding: '12px 16px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
  },
  categorySelect: {
    padding: '12px 16px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    minWidth: '150px',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#fca5a5',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorClose: {
    background: 'none',
    border: 'none',
    color: '#fca5a5',
    cursor: 'pointer',
    fontSize: '16px',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: '#94a3b8',
  },
  empty: {
    textAlign: 'center',
    padding: '64px 24px',
    color: '#94a3b8',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  emptyButton: {
    marginTop: '16px',
    padding: '12px 24px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  card: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '12px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      borderColor: 'var(--brand-primary, #3b82f6)',
      transform: 'translateY(-2px)',
    },
  },
  cardIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f1f5f9',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#94a3b8',
  },
  cardDate: {
    fontSize: '12px',
    color: '#64748b',
  },
  cardTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  tag: {
    padding: '2px 8px',
    background: '#334155',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#94a3b8',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    background: '#1e293b',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  detailModal: {
    background: '#1e293b',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '450px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #334155',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: '20px',
    cursor: 'pointer',
  },
  modalBody: {
    padding: '24px',
  },
  dropZone: {
    border: '2px dashed #334155',
    borderRadius: '8px',
    padding: '32px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  dropZoneLabel: {
    cursor: 'pointer',
    display: 'block',
  },
  dropZoneIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  dropZoneHint: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '8px',
  },
  fileInput: {
    display: 'none',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: '#0f172a',
    borderRadius: '8px',
  },
  fileName: {
    flex: 1,
    fontSize: '14px',
    color: '#f1f5f9',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  progressBar: {
    width: '60px',
    height: '4px',
    background: '#334155',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'var(--brand-primary, #3b82f6)',
    transition: 'width 0.3s',
  },
  metadata: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  select: {
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
  },
  input: {
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
  },
  textarea: {
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
    resize: 'vertical',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid #334155',
  },
  cancelButton: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
  },
  confirmButton: {
    padding: '10px 20px',
    background: 'var(--brand-primary, #3b82f6)',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  uploadingButton: {
    padding: '10px 20px',
    background: '#64748b',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontSize: '14px',
    cursor: 'not-allowed',
  },
  detailBody: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  detailIcon: {
    fontSize: '80px',
  },
  detailInfo: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#f1f5f9',
    textAlign: 'center',
    margin: 0,
    wordBreak: 'break-word',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#94a3b8',
  },
  detailLabel: {
    color: '#64748b',
  },
  detailDescription: {
    marginTop: '8px',
    paddingTop: '12px',
    borderTop: '1px solid #334155',
  },
  detailTags: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '12px',
    borderTop: '1px solid #334155',
  },
  detailTag: {
    display: 'inline-block',
    padding: '4px 10px',
    background: '#334155',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#94a3b8',
    margin: '2px',
  },
  deleteButton: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    color: '#ef4444',
    fontSize: '14px',
    cursor: 'pointer',
  },
  downloadButton: {
    padding: '10px 20px',
    background: '#10b981',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
